import React, { Fragment, useEffect, useState,
} from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import Decimal from 'decimal.js';
import {
  CloseOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  Table, Button, Drawer, Popconfirm, notification,
  Popover,
} from 'antd';
import H3Image from '@Uilib/h3Image';
import H3Text from '@Uilib/h3Text';
import PurchaseRequestActions from '@Actions/purchaseRequestActions';
import ProductActions from '@Actions/productActions';
import SellerActions from '@Actions/sellerActions';
import DepartmentActions from '@Actions/departmentActions';
import closeIcon from '@Images/icons/icon-close-blue.png';
import { v4 as uuidv4 } from 'uuid';
import SelectDepartment from '@Components/Common/SelectDepartment';
import BatchSelector from '@Components/Inventory/Indent/CreateIndent/BatchSelector';
import Helpers from '@Apis/helpers';
import { QUANTITY } from '@Apis/constants';
import TenantSelector from '@Components/Common/Selector/TenantSelector';
import DocConfigActions from '@Actions/docConfigActions';
import BulkPreviewST from '../BulkPreviewST';
import './style.scss';

const BulkSTForm = ({
  user,
  selectedRows,
  selectedTenants,
  multiplePurchaseRequests,
  getMultiplePurchaseRequests,
  departments,
  toggleBulkSTDrawer,
  getDocConfig,
  docConfigStockTransfer,
  getDocConfigStockTransferLoading,
  getProductById,
  getDepartments,
}) => {
  // Component states
  const [prLines, setPrLines] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showSTConfirmationDrawer, setShowSTConfirmationDrawer] = useState(false);
  const [call, setCall] = useState(false);
  const [tenantsDepartmentsData, setTenantDepartmentsData] = useState({});
  const isSecondaryUomEnabled = user?.tenant_info?.global_config?.settings?.enable_secondary_uom;

  const clearReservationsByLine = (lineKey) => {
    const staticReservations = JSON.parse(sessionStorage.getItem('staticReservations'));
    const staticReservationCopy = JSON.parse(JSON.stringify(staticReservations));
    Object.keys(staticReservationCopy)?.forEach((batchKey) => {
      if (staticReservationCopy?.[batchKey]?.[lineKey] !== undefined) {
        delete staticReservationCopy?.[batchKey]?.[lineKey];
      }
    });
    sessionStorage.setItem('staticReservations', JSON.stringify(staticReservationCopy));
  };

  const clearReservationsByBatchAndLine = (batchId, lineKey) => {
    const staticReservations = JSON.parse(sessionStorage.getItem('staticReservations'));
    const staticReservationCopy = JSON.parse(JSON.stringify(staticReservations));
    Object.keys(staticReservationCopy)?.forEach((batchKey) => {
      if (batchKey == batchId) {
        delete staticReservationCopy?.[batchKey]?.[lineKey];
      }
    });
    sessionStorage.setItem('staticReservations', JSON.stringify(staticReservationCopy));
  };

  // Handle item deletion
  const handleDelete = (key) => {
    const updatedPrLines = prLines?.filter((line) => line?.key !== key);

    clearReservationsByLine(key);

    setPrLines(updatedPrLines);
  };

  const handleTenantChange = (value, record, productData) => {
    const updatedPrLines = prLines?.map((item) => {
      if (item?.key === record?.key) {
        return {
          ...item,
          sourceTenantId: value, // Update sourceTenantId
          sourceTenantDepartmentId: null,
          sourceDepartmentId: null,
          sourceDepartmentName: null,
          product_batches: [],
          department_quantities: productData?.department_quantities || [],
        };
      }
      return item;
    });

    setPrLines(updatedPrLines); // Update the prLines state
  };

  const columns = [
    {
      title: '#',
      width: '30px',
      render: (record) => record?.sqn,
    },
    {
      title: 'Product',
      width: '110px',
      render: (record) => (
        <div style={{ width: '110px' }}>
          <div className="pr-selection__product-name">
            <Link to={`/inventory/product/view/${record?.product_sku_info?.product_sku_id}`} target="_blank">
              <H3Text className="table-subscript" text={record?.product_sku_info?.internal_sku_code} />
            </Link>
            {record?.product_sku_info?.product_sku_name || <H3Text text="New Product" className="pr-selection__new-product" />}
          </div>
        </div>
      ),
    },
    {
      title: 'Source Location',
      width: '160px',
      render: (record) => {

        const tenantId = record?.issuer_tenant_id ? record?.issuer_tenant_id : record?.tenant_id;

        const { userTenantDepartmentId, tenantDefaultStoreId } = Helpers.getUserTenantDepartmentIds(user, tenantId);

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '160px' }}>
            <TenantSelector
              hideTitle
              selectedTenant={record?.sourceTenantId}
              showSearch={false}
              onChange={(value) => {
                getProductById(value, record?.product_sku_info?.product_sku_id, '', (product) => {
                  if (!product) return;
                  handleTenantChange(value, record, product);
                });
              }}
              placeholder="Select Business Unit"
              labelClassName="form__input-row__label"
              includedTenants={Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.PURCHASE_REQUEST, Helpers.permissionTypes.CREATE)}
              disabled={userTenantDepartmentId !== tenantDefaultStoreId}
            />
            <SelectDepartment
              hideTitle
              emptyNotAllowed
              tenentLevelDepartment
              selectedDepartment={record?.sourceTenantDepartmentId}
              onChange={(value) => {
                updateDepartmentWithQuantity(value?.tenant_department_id, value?.department_id, value?.alias_name, record?.sourceTenantId, record);
              }}
              tenantId={record?.sourceTenantId}
              tenantsDepartmentsData= {tenantsDepartmentsData?.[record?.sourceTenantId]}
              excludedDepartments={record?.sourceTenantId === record?.tenant_id ? [record?.department_info?.department_id] : []}
            />
          </div>
        );
      },
      visible: true,
    },
    {
      title: 'Available Stock',
      width: '120px',
      render: (text, record) => {
        const filteredDepartmentsStock = record?.department_quantities?.filter((item) => item?.tenant_department_id !== record?.source_tenant_department_id);
        // const totalStockInOtherDepartments = filteredDepartmentsStock?.map((dept) => dept?.quantity).reduce((acc, cur) => acc + cur, 0);
        const totalStockInOtherDepartments = QUANTITY(filteredDepartmentsStock?.map((dept) => dept?.quantity).reduce((acc, cur) => acc + cur, 0) / record?.uom_info?.ratio, record?.uom_info?.precision);
        const hoverContent = (
          <div>
            {filteredDepartmentsStock?.map((dept) => (
              <div key={dept?.department_id} className="other-uom-stock">
                <H3Text text={dept?.alias_name} />
                <H3Text
                  // text={`${QUANTITY(dept?.quantity, record?.uom_info?.precision)}  ${record?.uom_info?.uqc?.toProperCase() || ''} `}
                  text={`${QUANTITY(dept?.quantity * (record?.product_sku_info?.uom_info?.ratio || 1) / (record?.uom_info?.ratio || 1) || 0, record?.product_sku_info?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase()}`}
                  className="tenant-inventory-slider__dept-qty"
                />
              </div>
            ))}
          </div>
        );
        return (
          <div style={{ width: '120px' }}>
            {Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => (item?.batch_in_use && (record?.isGoodStockOnly ? !item?.is_rejected_batch : true) && !item?.is_reserved)), 'available_qty')
              ? `${QUANTITY(Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => (item?.batch_in_use && (record?.isGoodStockOnly ? !item?.is_rejected_batch : true) && !item?.is_reserved)), 'available_qty') / record?.uom_info?.ratio, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase()}` : <span style={{ color: 'red', fontSize: '11px', whiteSpace: 'nowrap' }}>OUT OF STOCK</span>}
            {Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => (item?.batch_in_use && (record?.isGoodStockOnly ? !item?.is_rejected_batch : true) && !item?.is_reserved)), 'available_qty') > 0 && (
              <H3Text
                text={`In ${record?.product_batches?.filter((item) => (item?.batch_in_use && (record?.isGoodStockOnly ? !item?.is_rejected_batch : true) && !item?.is_reserved))?.length || '0'} Batches`}
                className="table-subscript"
              />
            )}
            {totalStockInOtherDepartments > 0 && (
              <Popover
                placement="bottom"
                title="Stocks in other locations"
                content={hoverContent}
                trigger="hover"
              >
                <div
                  className="table-subscript"
                  style={{
                    color: 'rgb(109 107 106)',
                  }}
                >
                  {`+${QUANTITY(totalStockInOtherDepartments, record?.uom_info?.precision)}  ${record?.uom_info?.uqc?.toProperCase() || ''} in other locations`}
                  <InfoCircleOutlined style={{
                    color: 'rgb(45, 124, 246)',
                    margin: '2px',
                  }}
                  />
                </div>
              </Popover>
            )}
            <div
              style={{
                marginTop: '8px',
              }}
            >
              {(record?.tenant_product_id || record?.secondary_uom_dept_qty) && (
                <Fragment>
                  {`${(record?.secondary_uom_dept_qty?.[record?.source_tenant_department_id] || 'N/A')} ${record?.product_sku_info?.secondary_uom_info?.uqc?.toProperCase() || ''}`}
                  {' '}
                  (Secondary)
                </Fragment>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Destination Location',
      width: '160px',
      render: (text, record) => (
        <div style={{ width: '160px' }}>
          <Link to={`/purchase/purchase-request/view/${record?.purchase_requisition_id}`} target="_blank">
            <H3Text className="table-subscript" text={record?.pr_number} />
          </Link>
          {`${record?.tenant_info?.tenant_name} (${record?.department_info?.department_name})`}
        </div>
      ),
    },
    {
      title: 'REQUESTED QTY',
      width: '110px',
      render: (text, record) => {
        const currentPRLine = prLines.find((prLine) => prLine.key === record?.key);
        return (
          <div style={{ width: '110px' }}>
            {`${QUANTITY(record?.requested_qty, record?.uom_info?.precision)} ${currentPRLine?.uom_info?.uqc?.toProperCase()}`}
          </div>
        );
      },
    },
    {
      title: 'PENDING QTY',
      width: '110px',
      render: (text, record) => {
        const currentPRLine = prLines?.find((prLine) => prLine?.key === record?.key);
        return (
          <div style={{ width: '110px' }}>
            {`${QUANTITY(record?.pending_qty, record?.uom_info?.precision)} ${currentPRLine?.uom_info?.uqc?.toProperCase()}`}
          </div>
        );
      },
    },
    {
      title: 'Transfer QTY',
      width: '120px',
      render: (text, record) => {

        const allowAccessTransferFromPr = user?.tenant_info?.purchase_config?.sub_modules?.purchase_request?.settings?.allow_excess_transfer_from_material_request;

        const copyData = JSON.parse(JSON.stringify(prLines));
        const currentPRLine = copyData?.find((prLine) => prLine?.key === record?.key);

        let errorMessage = '';
        let availableQty = new Decimal(0);
        let availableBatchQty = new Decimal(0);

        // Helper function to calculate if the quantity is valid

        const filteredBatches = record?.product_batches?.filter((item) =>
          item?.batch_in_use &&
          (currentPRLine.isGoodStockOnly ? !item?.is_rejected_batch : true) &&
          !item?.is_reserved
        );

        const totalAvailableQty = Helpers.getValueTotalInObject(filteredBatches, 'available_qty');
        const ratio = new Decimal(record?.uom_info?.ratio || 1);
        const precision = record?.uom_info?.precision || 2;

        availableQty = new Decimal(totalAvailableQty).dividedBy(ratio).toDecimalPlaces(precision);

        const getEntries = (obj) => (obj ? Object.entries(obj) : []);

        const staticReservations = JSON.parse(sessionStorage.getItem('staticReservations')) || {};

        const batchIdsToSum = record?.product_batches?.filter((item) =>
          item?.batch_in_use &&
          (currentPRLine.isGoodStockOnly ? !item?.is_rejected_batch : true)
        )?.map((item) => item?.batch_id) || [];

        const valuesToSum = batchIdsToSum.flatMap((batchId) =>
          getEntries(staticReservations[batchId])
            .filter(([lineId]) => lineId !== record?.key)
            .map(([_, value]) => new Decimal(value || 0))
        );

        const sum = valuesToSum.reduce((acc, value) => acc.plus(value), new Decimal(0));
        availableBatchQty = availableQty.minus(sum);

        const isQuantityInvalid = (lineRecord) => {
          const quantity = new Decimal(lineRecord?.quantity || 0);

          const invalidFormQuantity = formSubmitted && quantity.lte(0);
          if (invalidFormQuantity) {
            errorMessage = 'quantity should be greater then 0';
            return true;
          }

          const pendingQty = new Decimal(lineRecord?.pending_qty || 0);
          const pendingQtyLessThanZero = allowAccessTransferFromPr ? false : pendingQty.minus(quantity).lt(0);
          if (pendingQtyLessThanZero) {
            errorMessage = 'transfer quantity greater then pending quantity';
            return true;
          }

          const quantityGreaterThanAvailableQty = availableBatchQty.minus(quantity).lt(0);
          if (quantityGreaterThanAvailableQty) {
            errorMessage = 'transfer quantity greater then available quantity';
            return true;
          }

          errorMessage = '';
          return false;
        };

        // Helper function to handle the input change
        const handleInputChange = (event) => {
          const batches = JSON.parse(JSON.stringify(record?.product_batches));
          const { isGoodStockOnly } = currentPRLine;
          const pBatches = getBatches(batches, event.target.value, currentPRLine.source_tenant_department_id, currentPRLine, isGoodStockOnly, record?.key);

          const updatedData = copyData?.map((item) => {
            // Check if the item matches the record key
            if (item?.key === record?.key) {
              // Directly update the quantity and product_batches at the line level
              item.quantity = event.target.value;
              item.product_batches = pBatches;
            }
            return item;
          });

          setPrLines(updatedData);
        };
        return (
          <React.Fragment>
            <div style={{ width: '120px' }}>
              {isSecondaryUomEnabled && <span className="uom-label">{`Primary (${record?.uom_info?.uqc || 'N/A'})`}</span>}
              <input
                value={record?.quantity}
                type="Number"
                onWheel={(event) => event.target.blur()}
                onChange={handleInputChange}
                className="orgFormInput"
              />
              {(record?.quantity >= 0 || !!(record?.quantity)) && isQuantityInvalid(record) && (
                <div className="input-error">
                  {`*${errorMessage}`}
                </div>
              )}
            </div>
            {isSecondaryUomEnabled && record?.product_sku_info?.secondary_uom_id && (
              <div style={{ width: '120px', marginTop: '5px' }}>
                <span className="uom-label">{`Secondary (${record?.product_sku_info?.secondary_uom_info?.uqc ? record?.product_sku_info?.secondary_uom_info?.uqc : 'N/A'})`}</span>
                <input
                  value={record?.secondary_uom_qty}
                  type="Number"
                  onWheel={(event) => event.target.blur()}
                  onChange={(event) => {
                    const newQuantity = Number(event.target.value);
                    const updatedData = copyData?.map((item) => {
                      if (item?.key === record?.key) {
                        item.secondary_uom_qty = newQuantity;
                      }
                      return item;
                    });
                    setPrLines(updatedData);
                  }}
                  className="orgFormInput"
                />
              </div>
            )}
          </React.Fragment>
        );
      },
    },
    {
      title: '',
      width: '30px',
      render: (text, record) => (
        <div style={{ width: '20px' }}>
          <Popconfirm
            placement="topRight"
            title="Are you sure exclude this item from purchase orders? You will not be able to add it back."
            onConfirm={() => handleDelete(record?.key)}
            okText="Yes"
            cancelText="No"
          >
            <div className="table__delete-button">
              <CloseOutlined />
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const getBatches = (
    oldBaches,
    quantity,
    department,
    prLine,
    isGoodStockOnly,
    lineKey,
    resetNotRequired,
  ) => {
    const batches = [];
    let remainingQty = new Decimal(Number(quantity));
    const staticReservations = JSON.parse(sessionStorage.getItem('staticReservations'));
    const staticReservationCopy = JSON.parse(JSON.stringify(staticReservations));

    const clearLineReservations = () => {
      Object.keys(staticReservationCopy)?.forEach((batchKey) => (
        delete staticReservationCopy?.[batchKey]?.[lineKey]
      ));
    };

    clearLineReservations();

    const getBatchReservations = (batchId) => {
      let total = new Decimal(0);
      if (
        Object.prototype.hasOwnProperty.call(staticReservationCopy, Number(batchId)) &&
        Object.keys(staticReservationCopy[batchId])?.length > 0
      ) {
        Object.keys(staticReservationCopy[batchId])?.forEach((item) => {
          total = total.plus(new Decimal(staticReservationCopy[batchId][item] || 0));
        });
      }
      return {
        total: total.toNumber(),
        reservations: staticReservationCopy[batchId],
      };
    };

    const addBatchReservation = (batchId, qty) => {
      const qtyDecimal = new Decimal(qty || 0);
      if (Object.prototype.hasOwnProperty.call(staticReservationCopy, batchId)) {
        if (Object.prototype.hasOwnProperty.call(staticReservationCopy[batchId], lineKey)) {
          const existingQty = new Decimal(staticReservationCopy[batchId][lineKey] || 0);
          staticReservationCopy[batchId][lineKey] = existingQty.plus(qtyDecimal).toNumber();
        } else {
          staticReservationCopy[batchId][lineKey] = qtyDecimal.toNumber();
        }
      } else {
        staticReservationCopy[batchId] = {
          [lineKey]: qtyDecimal.toNumber(),
        };
      }
    };

    for (let i = 0; i < oldBaches?.length; i++) {
      if (oldBaches[i]?.tenant_department_id == department) {
        const batchReservations = getBatchReservations(oldBaches[i]?.batch_id);
        batches.push({
          ...oldBaches[i],
          consumed_qty: new Decimal(0).toNumber(),
          batch_in_use: !resetNotRequired ? oldBaches[i].batch_in_use : true,
          _reserved: batchReservations,
        });
      }
    }

    for (let i = 0; i < batches.length; i++) {
      if (isGoodStockOnly && batches[i]?.is_rejected_batch) continue;

      const baseQty = new Decimal(batches[i]?.available_qty || 0);
      const fromRatio = new Decimal(prLine?.uom_info?.ratio || 1);
      const toRatio = new Decimal(prLine?.product_sku_info?.uom_info?.ratio || 1);
      const reservedQty = new Decimal(batches[i]?._reserved?.total || 0);

      const avlQty = Decimal.max(baseQty.div(fromRatio).times(toRatio).minus(reservedQty), 0);

      if (batches[i]?.batch_in_use) {
        if (remainingQty.gt(0) && new Decimal(batches[i]?.consumed_qty || 0).lt(avlQty)) {
          const qtyToUse = Decimal.min(avlQty, remainingQty);
          batches[i].consumed_qty = qtyToUse.toNumber();
          remainingQty = remainingQty.minus(qtyToUse);
          addBatchReservation(batches[i]?.batch_id, qtyToUse);
        } else {
          batches[i].consumed_qty = 0;
          remainingQty = remainingQty.minus(0);
        }
      }
    }
    sessionStorage.setItem('staticReservations', JSON.stringify(staticReservationCopy));

    const finalBatches = batches.map((item) => ({
      ...item,
      _reserved: getBatchReservations(item?.batch_id),
    }));

    return finalBatches;
  };

  const createTenantPr = (prLine, row, isGoodStockOnly, batchConsumptionMethod, key) => {
    const lineKey = key || uuidv4();

    const sourceDepartmentId = prLine?.sourceDepartmentId;
    const sourceTenantDepartmentId = prLine?.sourceTenantDepartmentId;

    const sortedProductBatches = Helpers.batchSelectionMethod(batchConsumptionMethod, prLine?.product_batches, 0, isGoodStockOnly);
    const sourceTenantProductId = prLine?.product_batches?.length ? prLine?.product_batches?.[0]?.tenant_product_id : '';

    const quantity = Math.min(
      Helpers.getValueTotalInObject(
        getBatches(prLine?.product_batches, prLine?.quantity, sourceTenantDepartmentId, prLine, isGoodStockOnly, lineKey, true)?.filter(
          (batch) => batch?.batch_in_use,
        ),
        'available_qty',
      ),
      prLine?.quantity - prLine?.st_gen_quantity,
    );

    return {
      key: lineKey,
      availableQuantity: prLine?.available_qty,
      department_info: row?.department_info,
      product_sku_name: prLine?.product_sku_name,
      purchase_requisition_id: row?.purchase_requisition_id,
      pr_number: row?.pr_number,
      tenant_info: row?.tenant_info,
      issuer_tenant_id: row?.issuer_tenant_id,
      tenant_id: row?.tenant_id,
      tenant_product_id: sourceTenantProductId,
      st_gen_quantity: prLine?.st_gen_quantity,
      tax_info: prLine?.product_sku_info?.tax_info,
      pending_qty: (prLine?.original_qty || prLine?.quantity) - prLine?.st_gen_quantity,
      requested_qty: (prLine?.original_qty || prLine?.quantity),
      original_qty: (prLine?.original_qty || prLine?.quantity), // may be needed
      product_sku_info: prLine?.product_sku_info,
      department_quantities: prLine?.department_quantities,
      secondary_uom_dept_qty: prLine?.secondary_uom_dept_qty,
      uom_info: prLine?.uom_info,
      uom_name: prLine?.uom_name,
      uqc: prLine?.uqc,
      asset: prLine?.product_sku_info?.assets,
      expiry_days: prLine?.product_sku_info?.expiry_days,
      sourceTenantDepartmentId: prLine?.sourceTenantDepartmentId,
      sourceDepartmentId,
      sourceDepartmentName: prLine?.sourceDepartmentName,
      sourceTenantId: prLine?.sourceTenantId,
      destination_tenant_department_id: row?.tenant_department_id,
      source_tenant_department_id: sourceTenantDepartmentId,
      product_batches: getBatches(
        sortedProductBatches,
        quantity,
        sourceTenantDepartmentId,
        prLine,
        isGoodStockOnly,
        lineKey,
        true,
      ),
      quantity: Math.max(quantity, 0),
      pr_line_id: prLine?.pr_line_id,
      mo_line_id: prLine?.mo_line_id,
      tenant_department_id: row?.tenant_department_id,
      is_good_stock_only: isGoodStockOnly,
      batch_consumption_method: batchConsumptionMethod,
      expiryDateFormat: prLine?.product_sku_info?.expiry_date_format,
      manufacturingDateFormat: prLine?.product_sku_info?.manufacturing_date_format,
      isGoodStockOnly,
      batchConsumptionMethod,
    };
  };

  const processPurchaseRequests = (purchaseRequests) => {
    const lines = [];
    purchaseRequests?.forEach((row) => {
      const sourceTenantId =  row?.issuer_tenant_id ? row?.issuer_tenant_id : row?.tenant_id;
      let sourceTenantDepartmentId = row?.issuer_tenant_department_id ? row?.issuer_tenant_department_id : Helpers?.getDefaultTenantDepartmentIdForSelectedTenant(user, sourceTenantId);
      let sourceDepartmentId = row?.issuer_department_info?.department_id
        ? row?.issuer_department_info.department_id
        : tenantsDepartmentsData?.[sourceTenantId]?.find(
            (i) => i.tenant_department_id === sourceTenantDepartmentId
          )?.department_id;

      let sourceDepartmentName = row?.issuer_department_info?.department_name
        ? row?.issuer_department_info.department_name
        : tenantsDepartmentsData?.[sourceTenantId]?.find(
            (i) => i.tenant_department_id === sourceTenantDepartmentId
          )?.alias_name;

      if(sourceTenantDepartmentId === row?.tenant_department_id){
        sourceTenantDepartmentId = null;
        sourceDepartmentId = null;
        sourceDepartmentName = '';
      }

      row?.purchase_requistion_lines?.forEach((purchaseRequisitionLine) => {
        const prLine = {
          ...purchaseRequisitionLine,
          product_batches: purchaseRequisitionLine?.product_batches || [],
          department_quantities: purchaseRequisitionLine?.department_quantities || [],
          secondary_uom_dept_qty: purchaseRequisitionLine?.secondary_uom_dept_qty || [],
          expiryDateFormat: purchaseRequisitionLine?.product_sku_info?.expiry_date_format,
          manufacturingDateFormat: purchaseRequisitionLine?.product_sku_info?.manufacturing_date_format,
          sourceTenantId,
          sourceTenantDepartmentId,
          sourceDepartmentId,
          sourceDepartmentName,
        };
        if (prLine?.product_sku_info) {
          const isGoodStockOnly = true;
          const batchConsumptionMethod = Helpers.getBatchMethod(prLine?.product_sku_info);

          const tenantPr = createTenantPr(prLine, row, isGoodStockOnly, batchConsumptionMethod);

          lines.push(tenantPr);
        }
      });
    });
    return lines;
  };

  useEffect(() => {
    const tenantIds = user?.user_tenants?.map(i => i?.tenant_id);
    getDepartments('', '', 1, 100, null, (data) => {
      setTenantDepartmentsData(data?.data?.result?.tenant_wise_departments || {});
    }, null, null, tenantIds);
  }, []);


  // Initialize data fetching and processing
  useEffect(() => {

    if (!tenantsDepartmentsData) return; 

    const prIds = selectedRows?.map((row) => row?.purchase_requisition_id)?.join(',');

    getMultiplePurchaseRequests({ prIds, tenantIds: selectedTenants }, (purchaseRequests) => {
      if (purchaseRequests) {
        const initialPrLines = processPurchaseRequests(purchaseRequests?.purchase_requisition);
        setPrLines(initialPrLines);
      }
    });
    sessionStorage.setItem('staticReservations', JSON.stringify({}));

  }, [tenantsDepartmentsData, user, selectedRows, selectedTenants, call]);

  const updateDepartmentWithQuantity = (
    tenantDepartmentId,
    departmentId,
    departmentName,
    tenantId,
    record,
  ) => {
    const { key, isGoodStockOnly, batchConsumptionMethod, product_sku_info,} = record;

    clearReservationsByLine(key);

    const productSkuId = product_sku_info?.product_sku_id;
    const effectiveTenantId = tenantId || user?.tenant_info?.tenant_id;

    // Check if matching PR data is available to avoid API call
    const matchingPR = multiplePurchaseRequests?.purchase_requisition?.find(
      (pr) => pr?.purchase_requisition_id === record?.purchase_requisition_id,
    );

    const prTenantId = matchingPR?.issuer_tenant_id || matchingPR?.tenant_id;

    const matchingPRLine = matchingPR?.purchase_requistion_lines?.find(
      (line) => line?.pr_line_id === record?.pr_line_id,
    );

    const useLocalProduct = matchingPRLine && prTenantId === effectiveTenantId;

    const applyUpdate = (productData) => {
      const updatedRecord = {
        ...record,
        product_batches: productData?.product_batches || [],
        department_quantities: productData?.department_quantities || [],
        sourceDepartmentId: departmentId,
        sourceTenantDepartmentId: tenantDepartmentId,
        sourceDepartmentName: departmentName,
        isGoodStockOnly,
        batchConsumptionMethod,
      };

      const line = createTenantPr(
        updatedRecord,
        updatedRecord,
        isGoodStockOnly,
        batchConsumptionMethod,
        key,
      );

      if (!line) return;

      const updatedPrLines = prLines?.map((item) => {
        if (item?.key === line?.key) return line;
        return item;
      });

      setPrLines(updatedPrLines);
    };

    if (useLocalProduct) {
      applyUpdate(matchingPRLine);
    } else {
      getProductById(effectiveTenantId, productSkuId, tenantDepartmentId, (product) => {
        if (!product) return;
        applyUpdate(product);
      });
    }
  };

  // Validate form data
  const isDataValid = (noNotification) => {
    let isValidData = true;

    const isValidQty = prLines?.every((prLine) => prLine?.quantity > 0);

    if (!isValidQty) {
      !noNotification && notification.error({
        message: 'Please enter quantity for every product.',
        duration: 4,
        placement: 'top',
      });
      return false;
    }

    const allowAccessTransferFromPr = user?.tenant_info?.purchase_config?.sub_modules?.purchase_request?.settings?.allow_excess_transfer_from_material_request;

    const copyData = JSON.parse(JSON.stringify(prLines));

    copyData.forEach((record) => {
      const sourceTenantDepartment = record?.source_tenant_department_id;
      const sourceTenantId =  record?.sourceTenantId;
      const destinationTenant = record?.tenant_info?.tenant_id;
      const destinationTenantDepartment = record?.tenant_department_id;

      if (isValidData && sourceTenantId == destinationTenant && sourceTenantDepartment == destinationTenantDepartment) {
        !noNotification && notification.error({
          message: `Source and Destination department must be different for Product - ${record?.product_sku_info?.internal_sku_code} and #PR - ${record?.pr_number}`,
          duration: 4,
          placement: 'top',
        });
        isValidData = false;
      }
      let availableQty = 0;
      let availableBatchQty = {};
      // availableQty = Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => (item?.batch_in_use && (record?.isGoodStockOnly ? !item?.is_rejected_batch : true))), 'available_qty');

      availableQty = QUANTITY(Helpers.getValueTotalInObject(record?.product_batches?.filter((item) => (item?.batch_in_use && (record?.isGoodStockOnly ? !item?.is_rejected_batch : true) && !item?.is_reserved)), 'available_qty') / record?.uom_info?.ratio, record?.uom_info?.precision);

      const getEntries = (obj) => (obj ? Object.entries(obj) : []);

      const staticReservations = JSON.parse(sessionStorage.getItem('staticReservations'));

      const batchIdsToSum = record?.product_batches?.filter((item) => (item?.batch_in_use && (record?.isGoodStockOnly ? !item?.is_rejected_batch : true)))?.map((item) => item?.batch_id) || [];

      const valuesToSum = batchIdsToSum?.flatMap((batchId) => getEntries(staticReservations[batchId])
        .filter(([lineId, value]) => lineId !== record?.key)
        .map(([lineId, value]) => value));

      const sum = valuesToSum.reduce((acc, value) => acc + value, 0);
      availableBatchQty = availableQty - sum;

      const quantity = Number(record?.quantity || 0);

      const pendingQtyLessThanZero = allowAccessTransferFromPr ? false : Number(record?.pending_qty) - quantity < 0;

      if (isValidData && pendingQtyLessThanZero) {
        !noNotification && notification.error({
          message: 'transfer quantity greater then pending quantity',
          duration: 4,
          placement: 'top',
        });
        isValidData = false;
      }

      const quantityGreaterThanAvailableQty = (availableBatchQty - quantity) < 0;

      if (isValidData && quantityGreaterThanAvailableQty) {
        !noNotification && notification.error({
          message: 'transfer quantity greater then available quantity',
          duration: 4,
          placement: 'top',
        });
        isValidData = false;
      }
    });
    return isValidData;
  };

  // Confirm purchase
  const convertToST = () => {
    if (isDataValid()) {
      setShowSTConfirmationDrawer(true);
    } else {
      setFormSubmitted(true);
    }
  };
  return (
    <Fragment>
      <div className="pr-selection__preview-container">
        <div style={{ marginBottom: '80px' }} className="bulk-st-items-table">
          <div className="bulkpr-verify-title">
            <div>
              {`${prLines?.length || 0} Items in this Material Request`}
            </div>
          </div>

          <Table
            loading={!prLines?.length || getDocConfigStockTransferLoading}
            columns={columns}
            size="small"
            tableLayout="fixed"
            dataSource={prLines?.filter((item) => item?.product_sku_info)}
            pagination={false}
            bordered={false}
            expandable={{
              rowExpandable: (record) => Helpers.getValueTotalInObject(record?.product_batches, 'available_qty') > 0,
              expandedRowRender: (record) => (
                <React.Fragment>
                  <BatchSelector
                    batches={record?.product_batches}
                    uomInfo={record?.uom_info}
                    expiryDays={record?.expiry_days}
                    itemsRow={record}
                    productInfo={record}
                    noBlockCalculation
                    productUomInfo={record?.product_sku_info?.uom_info}
                    batchMethod={record?.batchConsumptionMethod}
                    updateBatchConsumptionMethod={(value) => {
                      const copyData = JSON.parse(JSON.stringify(prLines));

                      const updatedData = copyData.map((item) => {
                        if (record?.key === item.key) {
                          let batches = JSON.parse(JSON.stringify(item?.product_batches));

                          batches = batches.map((batch) => ({
                            ...batch,
                            consumed_qty: 0,
                            _reserved: {
                              total: 0,
                              reservations: {},
                            },
                          }));

                          clearReservationsByLine(item.key);

                          const sortedBatches = Helpers.batchSelectionMethod(
                            value,
                            batches,
                            0,
                            item.isGoodStockOnly
                          );

                          const pBatches = getBatches(
                            sortedBatches,
                            item?.quantity,
                            item?.source_tenant_department_id,
                            item,
                            item.isGoodStockOnly,
                            item.key,
                            false,
                          );

                          return {
                            ...item,
                            batchConsumptionMethod: value,
                            product_batches: pBatches,
                          };
                        }
                        return item;
                      });

                      setPrLines(updatedData);
                    }}
                    allStock={record?.isGoodStockOnly}
                    upDateAllStock={(value) => {
                      const copyData = JSON.parse(JSON.stringify(prLines));

                      const updatedData = copyData.map((item) => {
                        if (item?.key === record?.key) {
                          const batches = JSON.parse(JSON.stringify(item.product_batches));

                          const pBatches = getBatches(
                            batches,
                            item?.quantity,
                            item?.source_tenant_department_id,
                            item,
                            value,
                            item?.key,
                            false
                          );

                          return {
                            ...item,
                            isGoodStockOnly: value,
                            product_batches: pBatches,
                          };
                        }
                        return item;
                      });
                      setPrLines(updatedData);
                    }}
                    onToggleBatch={(record, itemsRow) => {
                      const copyData = JSON.parse(JSON.stringify(prLines));
                      const batchId = record?.batch_id;
                      const updatedData = copyData.map((item) => {
                        if (item?.key === itemsRow?.key) {
                          let qty = item.quantity;
                          const updatedBatches = item?.product_batches?.map((batch) => {
                            if (batch?.batch_id === batchId) {
                              qty -= batch?.consumed_qty;
                              return {
                                ...batch,
                                consumed_qty: 0,
                                batch_in_use: !batch?.batch_in_use,
                                _reserved: {
                                  total: 0,
                                  reservations: {},
                                },
                              };
                            }
                            return batch;
                          });
                          return {
                            ...item,
                            product_batches: updatedBatches,
                            quantity: qty,
                          };
                        }
                        return item;
                      });
                      clearReservationsByBatchAndLine(batchId, record?.key);
                      setPrLines(updatedData);
                    }}
                    innerBatchSelector // temporary
                  />
                </React.Fragment>
              ),
            }}
          />
        </div>
        <div className="pr-selection__footer-container">
          <div className="st-selection__footer-right">
            <Button type="primary" onClick={convertToST} disabled={!prLines?.length}>
              Verify Stock Transfer
            </Button>
          </div>
        </div>
        <Drawer
          open={showSTConfirmationDrawer}
          width="1200px"
          mask
          onClose={() => setShowSTConfirmationDrawer(false)}
          destroyOnClose
        >
          <div className="custom-drawer__header-wrapper">
            <div className="custom-drawer__header" style={{ width: '1150px' }}>
              <H3Text text="Confirm Stock Transfer" className="custom-drawer__title" />
              <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setShowSTConfirmationDrawer(false)} />
            </div>
          </div>
          <BulkPreviewST
            prLines={prLines}
            callback={() => {
              setShowSTConfirmationDrawer(false);
              toggleBulkSTDrawer();
            }}
          />
        </Drawer>
      </div>
    </Fragment>
  );
};
const mapStateToProps = ({
  UserReducers, PurchaseRequestReducers, DepartmentReducers, DocConfigReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  multiplePurchaseRequests: PurchaseRequestReducers.multiplePurchaseRequests,
  departments: DepartmentReducers.departments,
  docConfigStockTransfer: DocConfigReducers.docConfigStockTransfer,
  getDocConfigStockTransferLoading: DocConfigReducers.getDocConfigStockTransferLoading,
});

const mapDispatchToProps = (dispatch) => ({
  getMultiplePurchaseRequests: (params, callback) => dispatch(PurchaseRequestActions.getMultiplePurchaseRequests(params, callback)),
  getMultiplePurchaseRequestsSuccess: (multiplePurchaseRequests) => dispatch(PurchaseRequestActions.getMultiplePurchaseRequestsSuccess(multiplePurchaseRequests)),
  getDocConfig: (tenantId, entityName, callback) => dispatch(DocConfigActions.getDocConfig(tenantId, entityName, callback)),
  getProductById: (tenantId, productSkuId, tenantDepartmentId, callback) => dispatch(ProductActions.getProductById(tenantId, productSkuId, tenantDepartmentId, callback)),
  getDepartments: (keyword, tenantId, page, limit, departmentId, callback, orgId, isAdmin, multipleSelectedTenantIds) => dispatch(
    DepartmentActions.getDepartments(keyword, tenantId, page, limit, departmentId, callback, orgId, isAdmin, multipleSelectedTenantIds),
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BulkSTForm));
